import { Stack } from "@fluentui/react/lib/Stack";
import { WithTheme } from "Components/Generic/Theme/WithTheme";
import { FilterExpression } from "Utils/Filter";
import { AutoComplete } from "./AutoComplete";
import { Chip } from "./Chip";
import { ISearchProps } from "./ISearchProps";
import { SearchContextProvider, WithSearchContext } from "./SearchContext";
import { useSearchStyles } from "./UseSearchStyles";

const SearchInternal = WithTheme(
  WithSearchContext((props: ISearchProps): JSX.Element => {
    const {
      onEdit,
      onRemove,
      filterExpression,
      theme: { spacing }
    } = props;
    const styles = useSearchStyles();

    function renderExpressionGroup(expr: FilterExpression, depth = 0) {
      if (expr == null) {
        return null;
      }

      if (expr.type === `condition`) {
        return (
          <Chip
            key={`${expr.fieldName}:${expr.searchTerm}`}
            fieldName={expr.fieldName}
            onEdit={onEdit}
            onRemove={onRemove}
            search={expr.searchTerm}
          />
        );
      }

      if (expr.type === `group`) {
        return (
          <Stack
            className={`${styles.groupChip} ${expr.operator === `OR` ? styles.orGroup : styles.andGroup}`}
            horizontal
            wrap
            tokens={{ childrenGap: 8 }}
          >
            {expr.expressions.map((childExpr, index) => (
              <>
                {renderExpressionGroup(childExpr, depth + 1)}
                {index < expr.expressions.length - 1 && expr.operator === `OR` && (
                  <Stack.Item className={styles.operatorBadge}>OR</Stack.Item>
                )}
              </>
            ))}
          </Stack>
        );
      }

      return null;
    }

    return (
      <Stack tokens={{ childrenGap: spacing.s2 }}>
        <AutoComplete />
        <Stack.Item align="start">
          <Stack className={styles.chipWrap} horizontal wrap tokens={{ childrenGap: 10 }}>
            {renderExpressionGroup(filterExpression)}
          </Stack>
        </Stack.Item>
      </Stack>
    );
  })
);

export function Search(): JSX.Element {
  return (
    <SearchContextProvider>
      <SearchInternal />
    </SearchContextProvider>
  );
}
